import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, useAuth } from "@/App";
import { 
  ArrowLeft, Edit, Trash2, Car, User, Phone, Mail, 
  Calendar, FileText, Clock, Package, PackageCheck, 
  ArrowRight, Send, AlertTriangle, ExternalLink, Calculator, Receipt,
  CheckCircle, Wrench, MessageSquare, Camera, Gauge, Copy, Check, ClipboardCheck
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import VehiclePhotos from "./VehiclePhotos";
import VehicleComments from "./VehicleComments";
import { VehicleRentabilite, VehiclePriorite } from "./VehicleRentabilite";
import WorkflowStepper from "./WorkflowStepper";
import VehicleHistoryList from "./VehicleHistoryList";
import VehicleInternalDossier from "./VehicleInternalDossier";
import VehicleChecklist from "./VehicleChecklist";

// URLs d'intégration
const ATELIO_URL = "https://www.atelio-chiffrage.com/index.html";
const GTA_PRO_URL = "https://portail.gta-pro.fr/ORDI_3000_Portail/PAGE_Traitement/OIwAAGPjwNehAA";

export default function VehicleDetail() {
  const { user, hasActionAccess } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [workflowSteps, setWorkflowSteps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [advanceDialog, setAdvanceDialog] = useState(false);
  const [advanceNotes, setAdvanceNotes] = useState("");
  const [processing, setProcessing] = useState(false);
  const [piecesUpdating, setPiecesUpdating] = useState(false);
  
  // Dialog retour en arrière workflow
  const [revertDialog, setRevertDialog] = useState(false);
  const [revertNotes, setRevertNotes] = useState("");
  const [reverting, setReverting] = useState(false);
  
  // Edition description
  const [editingDescription, setEditingDescription] = useState(false);
  const [tempDescription, setTempDescription] = useState("");
  const [savingDescription, setSavingDescription] = useState(false);
  
  // Dialog envoi devis par SMS
  const [devisDialog, setDevisDialog] = useState(false);
  const [devisMessage, setDevisMessage] = useState("");
  const [sendingDevis, setSendingDevis] = useState(false);
  
  // Déblocage véhicule
  const [unblocking, setUnblocking] = useState(false);
  
  // Actions rapides collaborateur
  const [quickActionDialog, setQuickActionDialog] = useState(false);
  const [quickActionType, setQuickActionType] = useState("comment");
  const [quickComment, setQuickComment] = useState("");
  const [quickKm, setQuickKm] = useState("");
  const [quickPhoto, setQuickPhoto] = useState(null);
  const [savingQuickAction, setSavingQuickAction] = useState(false);
  const [checklistOpen, setChecklistOpen] = useState(false);
  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  // Permission-based access checks
  const canEditRdv = hasActionAccess("edit_rdv");
  const canDeleteRdv = hasActionAccess("delete_rdv");
  const canViewClientInfo = hasActionAccess("view_client_info");
  const canSendSms = hasActionAccess("send_sms");
  const canAdvanceWorkflow = hasActionAccess("advance_workflow");
  const canValidateStep = hasActionAccess("validate_step");
  const canAddComment = hasActionAccess("add_comment");
  const canMarkPieces = hasActionAccess("mark_pieces");
  
  // Permission pour reculer dans le workflow (admin ou secrétaire)
  const canRevertWorkflow = user?.role === "admin" || user?.specialite === "secretaire";

  const fetchData = async () => {
    setLoading(true);
    try {
      const vehicleRes = await api.get(`/vehicles/${id}`);
      const v = vehicleRes.data;
      setVehicle(v);
      // Fetch prestation-specific workflow steps
      const stepsRes = await api.get(`/workflow-steps?prestation=${encodeURIComponent(v.type_intervention)}&sub=${encodeURIComponent(v.sub_prestation || "")}`);
      setWorkflowSteps(stepsRes.data);
    } catch (error) {
      toast.error("Véhicule non trouvé");
      navigate(-1);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // SMS confirmation dialog
  const [smsConfirmDialog, setSmsConfirmDialog] = useState(false);
  const [sendingSms, setSendingSms] = useState(false);

  const handleAdvanceWorkflow = async () => {
    if (!vehicle) return;
    setProcessing(true);
    try {
      let response;
      if (vehicle.workflow_step === "none") {
        response = await api.post(`/workflow/complete-mecanique/${vehicle.id}`);
      } else {
        response = await api.post("/workflow/advance", {
          vehicle_id: vehicle.id,
          notes: advanceNotes
        });
      }
      toast.success("Étape avancée avec succès !");
      setAdvanceDialog(false);
      setAdvanceNotes("");
      // Si le véhicule est maintenant terminé, proposer d'envoyer le SMS
      const updated = response.data;
      if (updated?.statut === "termine") {
        fetchData();
        setSmsConfirmDialog(true);
      } else {
        fetchData();
      }
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur lors de l'avancement");
    }
    setProcessing(false);
  };

  const handleSendTermineSms = async () => {
    setSendingSms(true);
    try {
      await api.post(`/workflow/send-termine-sms/${vehicle.id}`);
      toast.success("SMS 'véhicule prêt' envoyé au client");
      setSmsConfirmDialog(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur envoi SMS");
    }
    setSendingSms(false);
  };

  const handleUpdatePieces = async (value) => {
    setPiecesUpdating(true);
    try {
      await api.put(`/vehicles/${id}/pieces?pieces_commandees=${value}`);
      setVehicle(prev => ({ ...prev, pieces_commandees: value }));
      toast.success(value ? "Pièces marquées comme commandées" : "Pièces à commander");
    } catch (error) {
      toast.error("Erreur");
    }
    setPiecesUpdating(false);
  };

  const getNextStepLabel = () => {
    if (!vehicle) return "";
    if (vehicle.workflow_step === "none") {
      return "Terminé";
    }
    const stepNames = workflowSteps.map(s => s.name);
    const currentIdx = stepNames.indexOf(vehicle.workflow_step);
    if (currentIdx >= stepNames.length - 1) return "Terminé";
    return workflowSteps[currentIdx + 1]?.label || "Suivant";
  };

  const getCurrentStepLabel = () => {
    if (!vehicle) return "";
    if (vehicle.workflow_step === "none") return "Mécanique";
    if (vehicle.statut === "termine" || vehicle.workflow_step === "termine") return "Terminé";
    const step = workflowSteps.find(s => s.name === vehicle.workflow_step);
    return step?.label || vehicle.workflow_step;
  };

  const getPrevStepLabel = () => {
    if (!vehicle || !workflowSteps.length) return null;
    if (vehicle.workflow_step === "termine") {
      // Revenir à la dernière étape
      return workflowSteps[workflowSteps.length - 1]?.label || "Précédent";
    }
    const stepNames = workflowSteps.map(s => s.name);
    const currentIdx = stepNames.indexOf(vehicle.workflow_step);
    if (currentIdx <= 0) return null; // Déjà à la première étape
    return workflowSteps[currentIdx - 1]?.label || "Précédent";
  };

  const handleRevertWorkflow = async () => {
    setReverting(true);
    try {
      await api.post("/workflow/revert", {
        vehicle_id: id,
        notes: revertNotes || "Retour à l'étape précédente"
      });
      toast.success("Retour à l'étape précédente effectué");
      setRevertDialog(false);
      setRevertNotes("");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur lors du retour");
    }
    setReverting(false);
  };

  // Sauvegarder la description
  const handleSaveDescription = async () => {
    setSavingDescription(true);
    try {
      await api.put(`/vehicles/${id}`, { description: tempDescription });
      toast.success("Description enregistrée");
      setEditingDescription(false);
      fetchData();
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
    setSavingDescription(false);
  };

  // Démarrer l'édition de la description
  const startEditingDescription = () => {
    setTempDescription(vehicle?.description || "");
    setEditingDescription(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await api.delete(`/vehicles/${id}`);
      toast.success("Véhicule supprimé");
      navigate(-1);
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
    setDeleting(false);
  };

  const handleSendSMS = async (type) => {
    try {
      let endpoint = "";
      let successMsg = "";
      
      switch (type) {
        case "progress":
          endpoint = `/vehicles/${id}/notify-client`;
          successMsg = "SMS d'avancement envoyé au client";
          break;
        case "ready":
          endpoint = `/vehicles/${id}/notify-ready`;
          successMsg = "SMS 'véhicule prêt' envoyé au client";
          break;
        default:
          endpoint = `/vehicles/${id}/notify-client`;
          successMsg = "SMS envoyé au client";
      }
      
      const response = await api.post(endpoint);
      if (response.data.success) {
        toast.success(successMsg);
      } else {
        toast.error(response.data.message || "Erreur envoi SMS");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du SMS");
    }
  };

  const handleUnblockVehicle = async () => {
    setUnblocking(true);
    try {
      await api.put(`/vehicles/${id}/unblock`);
      toast.success("Véhicule débloqué avec succès");
      fetchData();
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = Array.isArray(detail) ? detail.map(d => d.msg || d.message).join(", ") : (typeof detail === 'object' ? JSON.stringify(detail) : detail);
      toast.error(msg || "Erreur lors du déblocage");
    }
    setUnblocking(false);
  };

  // Actions rapides - Ouvrir le dialog
  const openQuickAction = (type) => {
    setQuickActionType(type);
    setQuickComment("");
    setQuickKm(vehicle?.kilometrage || "");
    setQuickPhoto(null);
    setQuickActionDialog(true);
  };

  // Actions rapides - Capture photo
  const handleQuickPhotoCapture = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setQuickPhoto(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Actions rapides - Sauvegarder
  const handleSaveQuickAction = async () => {
    setSavingQuickAction(true);
    try {
      if (quickActionType === "km") {
        if (!quickKm) {
          toast.error("Veuillez saisir le kilométrage");
          setSavingQuickAction(false);
          return;
        }
        await api.put(`/vehicles/${id}/kilometrage`, { kilometrage: parseInt(quickKm) });
        toast.success("Kilométrage enregistré");
      } else {
        // Comment ou photo
        if (!quickComment && !quickPhoto) {
          toast.error("Veuillez saisir un commentaire ou ajouter une photo");
          setSavingQuickAction(false);
          return;
        }
        await api.post(`/vehicles/${id}/comments`, {
          vehicle_id: id,
          message: quickComment || "Photo ajoutée",
          is_blocking: false,
          photo_base64: quickPhoto || null
        });
        toast.success(quickPhoto ? "Photo ajoutée" : "Note ajoutée");
      }
      setQuickActionDialog(false);
      fetchData();
      if (quickActionType === "km" && vehicle?.type_intervention === "mecanique") {
        setChecklistOpen(true);
      }
    } catch (error) {
      toast.error("Erreur lors de l'enregistrement");
    }
    setSavingQuickAction(false);
  };

  const [copiedImmat, setCopiedImmat] = useState(false);
  const handleCopyImmat = () => {
    if (!vehicle?.immatriculation) return;
    navigator.clipboard.writeText(vehicle.immatriculation);
    setCopiedImmat(true);
    toast.success(`Immatriculation ${vehicle.immatriculation} copiée !`);
    setTimeout(() => setCopiedImmat(false), 2000);
  };

  const openAtelioChiffrage = () => {
    // Ouvrir Atelio Chiffrage dans un nouvel onglet
    // L'immatriculation est copiée dans le presse-papier pour faciliter la recherche
    if (vehicle?.immatriculation) {
      navigator.clipboard.writeText(vehicle.immatriculation);
      toast.success(`Immatriculation ${vehicle.immatriculation} copiée !`);
    }
    window.open(ATELIO_URL, '_blank');
  };

  const openGtaPro = () => {
    // Ouvrir GTA Pro pour facturation
    if (vehicle?.immatriculation) {
      navigator.clipboard.writeText(vehicle.immatriculation);
      toast.success(`Immatriculation ${vehicle.immatriculation} copiée !`);
    }
    window.open(GTA_PRO_URL, '_blank');
  };

  const handleSendDevis = async () => {
    if (!devisMessage.trim()) {
      toast.error("Veuillez écrire un message");
      return;
    }
    if (devisMessage.length > 160) {
      toast.error("Le message dépasse 160 caractères");
      return;
    }
    if (!vehicle?.client?.telephone) {
      toast.error("Numéro de téléphone client manquant");
      return;
    }
    
    setSendingDevis(true);
    try {
      const response = await api.post(`/vehicles/${id}/send-sms-custom`, {
        message: devisMessage
      });
      if (response.data.success) {
        toast.success("SMS envoyé !");
        setDevisDialog(false);
        setDevisMessage("");
      } else {
        toast.error(response.data.message || "Erreur lors de l'envoi");
      }
    } catch (error) {
      toast.error("Erreur lors de l'envoi du SMS");
    }
    setSendingDevis(false);
  };
  
  // Initialiser le message par défaut quand le dialog s'ouvre
  const openDevisDialog = () => {
    const defaultMsg = `Devis ${vehicle?.immatriculation}: `;
    setDevisMessage(defaultMsg);
    setDevisDialog(true);
  };

  const statusStyles = {
    en_attente: "bg-amber-100 text-amber-700",
    en_cours: "bg-blue-100 text-blue-700",
    termine: "bg-emerald-100 text-emerald-700",
  };

  const statusLabels = { 
    en_attente: "En attente", 
    en_cours: "En cours", 
    termine: "Terminé" 
  };

  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!vehicle) return null;

  return (
    <div className="p-6 max-w-5xl mx-auto animate-fade-in" data-testid="vehicle-detail">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate(-1)} data-testid="back-btn">
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-black text-slate-900 font-mono">
                {vehicle.immatriculation}
              </h1>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-slate-500 hover:text-red-600"
                onClick={handleCopyImmat}
                title="Copier l'immatriculation"
                data-testid="copy-immat-btn"
              >
                {copiedImmat ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />}
              </Button>
              <Badge className={statusStyles[vehicle.statut]}>
                {statusLabels[vehicle.statut]}
              </Badge>
            </div>
            <p className="text-slate-600">
              {vehicle.marque} {vehicle.modele} {vehicle.couleur && `- ${vehicle.couleur}`}
              {vehicle.kilometrage && (
                <span className="ml-2 text-sm text-slate-500">
                  • <Gauge className="h-3 w-3 inline" /> {vehicle.kilometrage.toLocaleString()} km
                </span>
              )}
            </p>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          {/* Boutons actions rapides pour collaborateurs */}
          {user?.role === "collaborateur" && vehicle.statut !== "termine" && (
            <div className="flex gap-1 mr-2">
              <Button
                variant="outline"
                size="sm"
                className="text-blue-600 border-blue-300 hover:bg-blue-50"
                onClick={() => openQuickAction("comment")}
                title="Ajouter une note"
              >
                <MessageSquare className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-purple-600 border-purple-300 hover:bg-purple-50"
                onClick={() => openQuickAction("photo")}
                title="Ajouter une photo"
              >
                <Camera className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                onClick={() => openQuickAction("km")}
                title="Saisir kilométrage"
              >
                <Gauge className="h-4 w-4" />
              </Button>
              {vehicle?.type_intervention === "mecanique" && (
                <Button
                  variant="outline"
                  size="sm"
                  className="text-slate-700 border-slate-300 hover:bg-slate-100"
                  onClick={() => setChecklistOpen(true)}
                  title="Check-list de contrôle"
                  data-testid="open-checklist-btn"
                >
                  <ClipboardCheck className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
          {(user?.role === "admin" || user?.specialite === "secretaire") && vehicle.type_intervention === "mecanique" && (
            <Button
              variant="outline"
              onClick={() => setChecklistOpen(true)}
              data-testid="open-checklist-btn-admin"
            >
              <ClipboardCheck className="mr-2 h-4 w-4" />
              Check-list
            </Button>
          )}
          {(canEditRdv || canDeleteRdv) && (
            <>
              {canEditRdv && (
                <Button variant="outline" onClick={() => navigate(`/vehicles/${id}/edit`)} data-testid="edit-btn">
                  <Edit className="mr-2 h-4 w-4" />
                  Modifier
                </Button>
              )}
              {canDeleteRdv && (
                <Button variant="outline" className="text-red-600 hover:bg-red-50" onClick={() => setDeleteOpen(true)} data-testid="delete-btn">
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer
                </Button>
              )}
            </>
          )}
        </div>
      </div>

      {/* Admin Actions Bar */}
      {(canAdvanceWorkflow || canMarkPieces) && (
        <Card className={`${vehicle.statut === "termine" ? "border-slate-300 bg-slate-50" : "border-amber-200 bg-amber-50"}`}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="flex items-center gap-4">
                {/* Pièces Status Toggle - seulement si pas terminé */}
                {canMarkPieces && vehicle.statut !== "termine" && (
                  <div className="flex items-center gap-3">
                    <Label className="text-sm font-semibold text-amber-900">Pièces:</Label>
                    <div className="flex items-center gap-2">
                      {vehicle.pieces_commandees ? (
                        <PackageCheck className="h-5 w-5 text-emerald-600" />
                      ) : (
                        <Package className="h-5 w-5 text-red-500" />
                      )}
                      <Switch
                        checked={vehicle.pieces_commandees}
                        onCheckedChange={handleUpdatePieces}
                        disabled={piecesUpdating}
                      />
                      <span className={`text-sm font-medium ${vehicle.pieces_commandees ? 'text-emerald-700' : 'text-red-600'}`}>
                        {vehicle.pieces_commandees ? "Commandées" : "À commander"}
                      </span>
                    </div>
                  </div>
                )}
                
                {vehicle.is_blocked && (
                  <div className="flex items-center gap-2">
                    <Badge className="bg-red-100 text-red-700">
                      <AlertTriangle className="h-4 w-4 mr-1" />
                      Dossier bloqué
                    </Badge>
                    {(user?.role === "admin" || user?.specialite === "secretaire") && (
                      <Button
                        onClick={handleUnblockVehicle}
                        disabled={unblocking}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700 text-white"
                        data-testid="unblock-vehicle-btn"
                      >
                        {unblocking ? <div className="spinner w-4 h-4" /> : "Débloquer"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
              
              {/* Advance Workflow Button */}
              {canAdvanceWorkflow && vehicle.statut !== "termine" && vehicle.workflow_step !== "none" && vehicle.workflow_step !== "termine" && (
                <Button
                  onClick={() => setAdvanceDialog(true)}
                  disabled={vehicle.is_blocked}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-advance-btn"
                >
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Avancer: {getCurrentStepLabel()} → {getNextStepLabel()}
                </Button>
              )}
              
              {/* Revert Workflow Button */}
              {canRevertWorkflow && (vehicle.statut === "termine" || (vehicle.workflow_step !== "none" && getPrevStepLabel())) && (
                <Button
                  onClick={() => setRevertDialog(true)}
                  variant="outline"
                  className="border-amber-500 text-amber-700 hover:bg-amber-50"
                  data-testid="admin-revert-btn"
                >
                  <ArrowLeft className="mr-2 h-4 w-4" />
                  Reculer: {getCurrentStepLabel()} → {getPrevStepLabel()}
                </Button>
              )}
              
              {vehicle.statut !== "termine" && vehicle.workflow_step === "none" && (
                <Button
                  onClick={() => setAdvanceDialog(true)}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="admin-complete-btn"
                >
                  <CheckCircle className="mr-2 h-4 w-4" />
                  Marquer terminé
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Workflow Progress */}
          {vehicle.workflow_step !== "none" && vehicle.workflow_step !== "termine" && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg">Progression</CardTitle>
              </CardHeader>
              <CardContent>
                <WorkflowStepper vehicle={vehicle} workflowSteps={workflowSteps} />
              </CardContent>
            </Card>
          )}

          {/* Description - Editable */}
          <Card className="border-slate-200">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Description / Notes
              </CardTitle>
              {canEditRdv && !editingDescription && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={startEditingDescription}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <Edit className="h-4 w-4 mr-1" />
                  Modifier
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {editingDescription ? (
                <div className="space-y-3">
                  <Textarea
                    value={tempDescription}
                    onChange={(e) => setTempDescription(e.target.value)}
                    placeholder="Ajouter des notes ou une description des travaux..."
                    className="min-h-[120px]"
                    autoFocus
                  />
                  <div className="flex gap-2 justify-end">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditingDescription(false)}
                      disabled={savingDescription}
                    >
                      Annuler
                    </Button>
                    <Button 
                      size="sm"
                      onClick={handleSaveDescription}
                      disabled={savingDescription}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {savingDescription ? <div className="spinner w-4 h-4" /> : "Enregistrer"}
                    </Button>
                  </div>
                </div>
              ) : (
                <p className="text-slate-700 whitespace-pre-wrap">
                  {vehicle.description || <span className="text-slate-400 italic">Aucune description - cliquez sur Modifier pour ajouter des notes</span>}
                </p>
              )}
            </CardContent>
          </Card>

          {/* Rentabilité - Admin/Secrétaire seulement */}
          {(user?.role === "admin" || user?.specialite === "secretaire") && (
            <VehicleRentabilite
              vehicleId={id}
              coutRevient={vehicle.cout_revient}
              montantFacture={vehicle.montant_facture}
              onUpdate={fetchData}
            />
          )}

          {/* Priorité - Carrosserie uniquement */}
          {vehicle.type_intervention !== "mecanique" && (user?.role === "admin" || user?.specialite === "secretaire") && (
            <VehiclePriorite
              vehicleId={id}
              priorite={vehicle.priorite}
              prioriteSemaine={vehicle.priorite_semaine}
              onUpdate={fetchData}
            />
          )}

          {/* Dossier interne - Notes et Documents (Admin/Secrétaire) */}
          {(user?.role === "admin" || user?.specialite === "secretaire") && (
            <VehicleInternalDossier vehicleId={id} />
          )}

          {/* Photos du véhicule */}
          <VehiclePhotos
            vehicleId={id}
            photos={vehicle.photos || []}
            canEdit={canEditRdv}
            onUpdate={fetchData}
          />

          {/* Commentaires / Signalements */}
          <VehicleComments 
            vehicleId={id}
            comments={vehicle.comments} 
            onUpdate={fetchData}
          />

          {/* History */}
          {vehicle.workflow_history?.length > 0 && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Clock className="h-5 w-5" />
                  Historique des étapes
                </CardTitle>
              </CardHeader>
              <CardContent>
                <VehicleHistoryList history={vehicle.workflow_history} />
              </CardContent>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Client Info - Hidden based on permissions */}
          {canViewClientInfo && (
            <Card className="border-slate-200">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <User className="h-5 w-5" />
                  Client
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <p className="font-semibold text-slate-900">
                    {vehicle.client.prenom} {vehicle.client.nom}
                  </p>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <Phone className="h-4 w-4" />
                  <a href={`tel:${vehicle.client.telephone}`} className="hover:text-blue-600">
                    {vehicle.client.telephone}
                  </a>
                </div>
                {vehicle.client.email && (
                  <div className="flex items-center gap-2 text-slate-600">
                    <Mail className="h-4 w-4" />
                    <a href={`mailto:${vehicle.client.email}`} className="hover:text-blue-600 truncate">
                      {vehicle.client.email}
                    </a>
                  </div>
                )}
                
                {/* SMS Buttons - Based on permissions */}
                {canSendSms && vehicle.statut !== "termine" && (
                  <div className="mt-4 pt-4 border-t border-slate-200 space-y-2">
                    <p className="text-xs font-semibold text-slate-700 mb-2">📱 Envoyer un SMS</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-blue-600 border-blue-300 hover:bg-blue-50"
                      onClick={() => handleSendSMS("progress")}
                      data-testid="sms-progress-btn"
                    >
                      <Send className="h-4 w-4 mr-2" />
                      Info avancement
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      onClick={() => handleSendSMS("ready")}
                      data-testid="sms-ready-btn"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Véhicule prêt
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* RDV Info */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Rendez-vous
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <p className="text-sm text-slate-500">Date prévue</p>
                <p className="font-semibold text-slate-900">
                  {format(parseISO(vehicle.date_rdv), "EEEE d MMMM yyyy", { locale: fr })}
                </p>
              </div>
              <Separator />
              <div>
                <p className="text-sm text-slate-500">Type d&apos;intervention</p>
                <Badge variant="outline" className="mt-1 capitalize">
                  {vehicle.type_intervention}
                </Badge>
              </div>
            </CardContent>
          </Card>

          {/* Outils - Atelio & GTA Pro */}
          {canSendSms && (
            <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-slate-50">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Wrench className="h-5 w-5 text-blue-600" />
                  Outils
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Atelio Chiffrage */}
                <Button
                  variant="outline"
                  className="w-full justify-start border-orange-300 hover:bg-orange-50 text-orange-700"
                  onClick={openAtelioChiffrage}
                  data-testid="open-atelio-btn"
                >
                  <Calculator className="h-4 w-4 mr-3" />
                  <div className="flex-1 text-left">
                    <p className="font-semibold">Atelio Chiffrage</p>
                    <p className="text-xs text-orange-600/70">Créer / voir le devis</p>
                  </div>
                  <ExternalLink className="h-4 w-4 text-orange-400" />
                </Button>
                
                {/* GTA Pro - Facturation */}
                <Button
                  variant="outline"
                  className={`w-full justify-start ${
                    vehicle.statut === "termine" || vehicle.workflow_step === "livraison"
                      ? "border-emerald-300 hover:bg-emerald-50 text-emerald-700"
                      : "border-slate-300 hover:bg-slate-50 text-slate-600"
                  }`}
                  onClick={openGtaPro}
                  data-testid="open-gta-btn"
                >
                  <Receipt className="h-4 w-4 mr-3" />
                  <div className="flex-1 text-left">
                    <p className="font-semibold">GTA Pro</p>
                    <p className={`text-xs ${
                      vehicle.statut === "termine" || vehicle.workflow_step === "livraison"
                        ? "text-emerald-600/70"
                        : "text-slate-500"
                    }`}>
                      {vehicle.statut === "termine" ? "Facturer ce véhicule" : "Ouvrir facturation"}
                    </p>
                  </div>
                  <ExternalLink className="h-4 w-4 opacity-50" />
                </Button>
                
                <Separator className="my-3" />
                
                {/* Envoyer SMS personnalisé */}
                <Button
                  variant="outline"
                  className="w-full justify-start border-purple-300 hover:bg-purple-50 text-purple-700"
                  onClick={openDevisDialog}
                  data-testid="send-devis-btn"
                >
                  <Send className="h-4 w-4 mr-3" />
                  <div className="flex-1 text-left">
                    <p className="font-semibold">Envoyer un SMS</p>
                    <p className="text-xs text-purple-600/70">Message personnalisé</p>
                  </div>
                </Button>
                
                <p className="text-[10px] text-slate-400 text-center pt-1">
                  L&apos;immatriculation est copiée automatiquement
                </p>
              </CardContent>
            </Card>
          )}

          {/* SMS Status */}
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle className="text-lg">Notifications SMS</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <div className="flex items-center justify-between text-sm">
                <span>Confirmation RDV</span>
                {vehicle.sms_sent?.confirmation ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Clock className="h-5 w-5 text-slate-300" />
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Rappel veille</span>
                {vehicle.sms_sent?.rappel ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Clock className="h-5 w-5 text-slate-300" />
                )}
              </div>
              <div className="flex items-center justify-between text-sm">
                <span>Véhicule prêt</span>
                {vehicle.sms_sent?.termine ? (
                  <CheckCircle className="h-5 w-5 text-emerald-500" />
                ) : (
                  <Clock className="h-5 w-5 text-slate-300" />
                )}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Delete Dialog */}
      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce véhicule ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Le véhicule {vehicle.immatriculation} et toutes ses données seront définitivement supprimés.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete"
            >
              {deleting ? <div className="spinner" /> : "Supprimer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Advance Workflow Dialog */}
      <AlertDialog open={advanceDialog} onOpenChange={setAdvanceDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Avancer le workflow</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <span className="font-mono font-bold text-slate-900 block">{vehicle.immatriculation}</span>
                  <span className="text-sm text-slate-600 block">{vehicle.marque} {vehicle.modele}</span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700">{getCurrentStepLabel()}</Badge>
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                  <Badge className="bg-emerald-100 text-emerald-700">{getNextStepLabel()}</Badge>
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-2">
            <Label className="text-sm font-medium">Notes (optionnel)</Label>
            <Textarea
              placeholder="Ajouter des notes..."
              value={advanceNotes}
              onChange={(e) => setAdvanceNotes(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={processing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAdvanceWorkflow}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-advance"
            >
              {processing ? <div className="spinner" /> : "Confirmer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Retour en arrière workflow */}
      <AlertDialog open={revertDialog} onOpenChange={setRevertDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-700">
              <ArrowLeft className="h-5 w-5" />
              Revenir à l&apos;étape précédente
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <div className="mt-3 p-3 bg-slate-50 rounded-lg">
                  <span className="font-mono font-bold text-slate-900 block">{vehicle.immatriculation}</span>
                  <span className="text-sm text-slate-600 block">{vehicle.marque} {vehicle.modele}</span>
                </div>
                <div className="mt-4 flex items-center gap-2">
                  <Badge className="bg-blue-100 text-blue-700">{getCurrentStepLabel()}</Badge>
                  <ArrowLeft className="h-4 w-4 text-amber-500" />
                  <Badge className="bg-amber-100 text-amber-700">{getPrevStepLabel()}</Badge>
                </div>
                <span className="mt-3 text-sm text-amber-600 block">
                  Cette action sera enregistrée dans l&apos;historique du véhicule.
                </span>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="py-2">
            <Label className="text-sm font-medium">Raison du retour (optionnel)</Label>
            <Textarea
              placeholder="Ex: Défaut constaté, pièce manquante..."
              value={revertNotes}
              onChange={(e) => setRevertNotes(e.target.value)}
              className="mt-1"
            />
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={reverting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevertWorkflow}
              disabled={reverting}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="confirm-revert"
            >
              {reverting ? <div className="spinner" /> : "Confirmer le retour"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Dialog Envoi SMS personnalisé */}
      <AlertDialog open={devisDialog} onOpenChange={setDevisDialog}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-purple-600" />
              Envoyer un SMS au client
            </AlertDialogTitle>
            <AlertDialogDescription>
              Écrivez votre message (160 caractères max)
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                <span className="font-semibold">{vehicle?.client?.prenom} {vehicle?.client?.nom}</span>
                <br />
                <span className="text-xs">{vehicle?.client?.telephone}</span>
              </p>
            </div>
            
            <div>
              <Label className="text-sm font-medium">Votre message</Label>
              <Textarea
                placeholder="Écrivez votre message ici..."
                value={devisMessage}
                onChange={(e) => setDevisMessage(e.target.value)}
                className="mt-1 min-h-[120px] resize-none"
                maxLength={160}
              />
              <div className="flex justify-between items-center mt-2">
                <p className="text-xs text-slate-500">
                  Astuce: collez votre lien Drive dans le message
                </p>
                <p className={`text-sm font-bold ${
                  devisMessage.length > 160 
                    ? "text-red-600" 
                    : devisMessage.length > 140
                    ? "text-amber-600"
                    : "text-emerald-600"
                }`}>
                  {devisMessage.length}/160
                </p>
              </div>
              {/* Barre de progression */}
              <div className="w-full h-2 bg-slate-200 rounded-full mt-2 overflow-hidden">
                <div 
                  className={`h-full transition-all ${
                    devisMessage.length > 160 
                      ? "bg-red-500" 
                      : devisMessage.length > 140
                      ? "bg-amber-500"
                      : "bg-emerald-500"
                  }`}
                  style={{ width: `${Math.min(100, (devisMessage.length / 160) * 100)}%` }}
                />
              </div>
            </div>
          </div>
          
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingDevis}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleSendDevis}
              disabled={sendingDevis || !devisMessage.trim() || devisMessage.length > 160}
              className="bg-purple-600 hover:bg-purple-700"
            >
              {sendingDevis ? <div className="spinner" /> : "Envoyer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Check-list de contrôle mécanique */}
      <VehicleChecklist
        vehicle={vehicle}
        open={checklistOpen}
        onOpenChange={setChecklistOpen}
        onSaved={fetchData}
      />


      {/* Dialog Actions rapides collaborateur */}
      <Dialog open={quickActionDialog} onOpenChange={setQuickActionDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {quickActionType === "km" && "Saisir le kilométrage"}
              {quickActionType === "comment" && "Ajouter une note"}
              {quickActionType === "photo" && "Ajouter une photo"}
            </DialogTitle>
            <DialogDescription>
              {vehicle?.immatriculation} - {vehicle?.marque} {vehicle?.modele}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {quickActionType === "km" && (
              <div>
                <Label className="flex items-center gap-2">
                  <Gauge className="h-4 w-4" />
                  Kilométrage
                </Label>
                <Input
                  type="number"
                  placeholder="Ex: 125000"
                  value={quickKm}
                  onChange={(e) => setQuickKm(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
            
            {(quickActionType === "comment" || quickActionType === "photo") && (
              <>
                <div>
                  <Label>Note / Commentaire</Label>
                  <Textarea
                    placeholder="Ajouter une note..."
                    value={quickComment}
                    onChange={(e) => setQuickComment(e.target.value)}
                    className="mt-1"
                  />
                </div>
                
                {quickActionType === "photo" && (
                  <div>
                    <Label>Photo</Label>
                    <div className="flex gap-2 mt-1">
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        ref={cameraInputRef}
                        onChange={handleQuickPhotoCapture}
                        className="hidden"
                      />
                      <input
                        type="file"
                        accept="image/*"
                        ref={galleryInputRef}
                        onChange={handleQuickPhotoCapture}
                        className="hidden"
                      />
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => cameraInputRef.current?.click()}
                        className="flex-1"
                      >
                        <Camera className="h-4 w-4 mr-2" />
                        Appareil photo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => galleryInputRef.current?.click()}
                        className="flex-1"
                      >
                        Galerie
                      </Button>
                    </div>
                    {quickPhoto && (
                      <div className="mt-2">
                        <img 
                          src={quickPhoto} 
                          alt="Preview" 
                          className="w-full max-h-48 object-cover rounded-lg"
                        />
                      </div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickActionDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={handleSaveQuickAction}
              disabled={savingQuickAction}
              className="bg-red-600 hover:bg-red-700"
            >
              {savingQuickAction ? <div className="spinner" /> : "Enregistrer"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog confirmation envoi SMS terminé */}
      <AlertDialog open={smsConfirmDialog} onOpenChange={setSmsConfirmDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-emerald-600" />
              Véhicule terminé — Envoyer le SMS ?
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <p className="mt-2">Le dossier <strong>{vehicle?.immatriculation}</strong> est marqué comme terminé.</p>
                <p className="mt-2">Voulez-vous envoyer un SMS au client <strong>{vehicle?.client?.prenom} {vehicle?.client?.nom}</strong> pour l'informer que son véhicule est prêt ?</p>
                <p className="mt-1 text-sm text-slate-500">Tél: {vehicle?.client?.telephone}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={sendingSms}>Non, pas maintenant</AlertDialogCancel>
            <AlertDialogAction onClick={handleSendTermineSms} disabled={sendingSms} className="bg-emerald-600 hover:bg-emerald-700">
              {sendingSms ? <div className="spinner" /> : "Oui, envoyer le SMS"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}
